import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { UUID } from "@/api/types";
import { useFolders } from "@/hooks/useFolders";
import { cn } from "@/lib/utils";

/**
 * Reusable folder-picker (folder-management-v3 §1 + §7).
 *
 * Single source of truth: the existing `useFolders()` hook, which wraps
 * `GET /api/folders` and exposes the user's allowed list. The FE never
 * filters that list further — what the BE returns is what the operator
 * may pick. Default selection follows §7 + Q5:
 *
 *   1. caller-provided `value`, if set;
 *   2. otherwise the active folder (`folders/active`), if it is in the
 *      returned list;
 *   3. otherwise the first item (BE sorts alphabetically by name).
 *
 * Empty list → disabled select with the Russian help-text
 * «Нет доступных папок — обратитесь к администратору». In that state the
 * picker emits no `onChange`, so the calling form should also disable its
 * submit button (the v2 §3.2 contract returns 400 anyway, but the FE
 * shouldn't even let the user try).
 *
 * Multi-select mode (TM-5d6213f1): when `multi=true`, the component
 * renders a checkbox list instead of a single-value <select>. The
 * `value`/`onChange` API switches accordingly:
 *
 *   - `multi=false` (default): `value: UUID | null`, `onChange(UUID)`.
 *   - `multi=true`: `value: UUID[]`, `onChange(UUID[])`. The auto-default
 *     adds the active folder (or the first item) to an empty list.
 */
export interface FolderPickerSingleProps {
  multi?: false;
  value: UUID | null;
  onChange: (folderId: UUID) => void;
  disabled?: boolean;
  id?: string;
  ariaInvalid?: boolean;
}

export interface FolderPickerMultiProps {
  multi: true;
  value: UUID[];
  onChange: (folderIds: UUID[]) => void;
  disabled?: boolean;
  id?: string;
  ariaInvalid?: boolean;
}

export type FolderPickerProps =
  | FolderPickerSingleProps
  | FolderPickerMultiProps;

export function FolderPicker(props: FolderPickerProps) {
  if (props.multi === true) return <FolderPickerMulti {...props} />;
  return <FolderPickerSingle {...props} />;
}

function FolderPickerSingle({
  value,
  onChange,
  disabled = false,
  id,
  ariaInvalid,
}: FolderPickerSingleProps) {
  const { t } = useTranslation();
  const { folders, activeFolderId, isLoading } = useFolders();

  // Auto-default: when no caller-supplied value, pick the active folder if
  // it is in the user's allowed list (admin path), else the first item
  // (Q5 — BE-sorted alphabetical). The effect runs once the folder list
  // is populated and the caller hasn't already nailed the choice.
  useEffect(() => {
    if (value !== null) return;
    if (folders.length === 0) return;
    const activeInList =
      activeFolderId !== null && folders.some((f) => f.id === activeFolderId);
    const next = activeInList
      ? (activeFolderId as UUID)
      : (folders[0]?.id as UUID | undefined);
    if (next !== undefined) onChange(next);
    // `onChange` is not in the dep array on purpose: a parent that
    // re-creates the closure each render would loop us forever. The
    // `value`/`folders`/`activeFolderId` triple is what should re-trigger
    // the defaulting, not the function identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, folders, activeFolderId]);

  const isEmpty = !isLoading && folders.length === 0;

  return (
    <div className="space-y-1">
      <select
        id={id}
        data-testid="folder-picker"
        className={cn(
          "h-9 w-full rounded-md border bg-background px-2 text-sm",
          isEmpty && "cursor-not-allowed opacity-60",
        )}
        value={value ?? ""}
        disabled={disabled || isEmpty}
        aria-invalid={ariaInvalid === true ? true : undefined}
        onChange={(e) => onChange(e.target.value as UUID)}
      >
        {value === null || folders.every((f) => f.id !== value) ? (
          // Render a placeholder when the caller's value is not (yet) in
          // the list — otherwise React warns about a controlled select
          // with no matching <option>.
          <option value="" disabled hidden>
            {t("common.dash")}
          </option>
        ) : null}
        {folders.map((f) => (
          <option
            key={f.id}
            value={f.id}
            data-testid={`folder-picker-option-${f.id}`}
          >
            {f.name}
          </option>
        ))}
      </select>
      {isEmpty ? (
        <p
          data-testid="folder-picker-empty"
          className="text-xs text-muted-foreground"
        >
          {t("folders.picker.empty")}
        </p>
      ) : null}
    </div>
  );
}

function FolderPickerMulti({
  value,
  onChange,
  disabled = false,
  id,
  ariaInvalid,
}: FolderPickerMultiProps) {
  const { t } = useTranslation();
  const { folders, activeFolderId, isLoading } = useFolders();
  // Track whether we've already auto-defaulted so that unticking the
  // last folder does NOT re-seed the picker (the operator should be
  // able to land on empty state and see the «выберите хотя бы одну
  // папку» helper).
  const didAutoDefault = useRef(false);

  useEffect(() => {
    if (didAutoDefault.current) return;
    if (value.length > 0) {
      didAutoDefault.current = true;
      return;
    }
    if (folders.length === 0) return;
    const activeInList =
      activeFolderId !== null && folders.some((f) => f.id === activeFolderId);
    const next = activeInList
      ? (activeFolderId as UUID)
      : (folders[0]?.id as UUID | undefined);
    if (next !== undefined) {
      didAutoDefault.current = true;
      onChange([next]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, folders, activeFolderId]);

  const isEmpty = !isLoading && folders.length === 0;
  const selected = new Set(value);

  function toggle(folderId: UUID): void {
    const next = new Set(selected);
    if (next.has(folderId)) next.delete(folderId);
    else next.add(folderId);
    onChange(Array.from(next));
  }

  return (
    <div className="space-y-1">
      <div
        id={id}
        role="group"
        data-testid="folder-picker-multi"
        aria-invalid={ariaInvalid === true ? true : undefined}
        className={cn(
          "max-h-40 overflow-y-auto rounded-md border bg-background p-2 text-sm",
          isEmpty && "cursor-not-allowed opacity-60",
        )}
      >
        {folders.map((f) => {
          const checked = selected.has(f.id);
          return (
            <label
              key={f.id}
              className="flex items-center gap-2 py-0.5"
              data-testid={`folder-picker-multi-option-${f.id}`}
            >
              <input
                type="checkbox"
                disabled={disabled || isEmpty}
                checked={checked}
                onChange={() => toggle(f.id)}
                data-testid={`folder-picker-multi-checkbox-${f.id}`}
              />
              <span>{f.name}</span>
            </label>
          );
        })}
      </div>
      {isEmpty ? (
        <p
          data-testid="folder-picker-empty"
          className="text-xs text-muted-foreground"
        >
          {t("folders.picker.empty")}
        </p>
      ) : null}
    </div>
  );
}
